import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { AddUsersToTeamDto, FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  teamSlug: string;
  roleGuid?: string;
  userGuids: string[];
};

const useAddUsersToTeamRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function addUsersToTeamRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: AddUsersToTeamDto = {
      roleGuid: variables.roleGuid,
      userGuids: variables.userGuids,
    };

    const response = await fetch(`${configuration.apiRootUrl}/teams/${variables.teamSlug}/members`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { addUsersToTeamRequest };
};

const useAddUsersToTeam = () => {
  const { addUsersToTeamRequest } = useAddUsersToTeamRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addUsersToTeamRequest,
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.teams.getTeams() });
      await queryClient.invalidateQueries({ queryKey: queryKeys.teams.getTeam(variables.teamSlug) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.teams.getTeamNetworkList(variables.teamSlug) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.teams.getTeamNetworkGraph(variables.teamSlug) });

      if (variables.roleGuid !== undefined) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.roles.getRole(variables.roleGuid) });
        await queryClient.invalidateQueries({ queryKey: queryKeys.roles.getRoleFeed(variables.roleGuid) });
      }
    },
  });
};

export { useAddUsersToTeam };
